/*
 * The teacher's own pupils (TDI-620): pure helpers behind "Your pupils" and
 * a pupil's forms page. Everything they read comes from rmsListTeacherTasks
 * (a teacher has no client reads): per pupil, the forms given to this
 * teacher with each form's status, percent and pathway.
 */

const FINISHED = ['completed', 'submitted', 'locked'];

export const PATHWAY_LABELS = { referral: 'Referral', sen: 'SEND pathway' };

/**
 * Done only once the form is finished: the strengths and needs questions
 * are all optional, so one answer can already read as 100 percent of a
 * draft (the same rule the task list has always used).
 */
export function isTeacherFormDone(form) {
  return FINISHED.includes(String((form && form.status) || '').toLowerCase());
}

/** 'done' | 'inProgress' | 'notStarted' for one form. */
export function teacherFormStatus(form) {
  if (isTeacherFormDone(form)) return 'done';
  const status = String((form && form.status) || '').toLowerCase();
  return Number(form && form.percent) > 0 || ['draft', 'in_progress'].includes(status)
    ? 'inProgress' : 'notStarted';
}

/**
 * One pupil row: how many of the teacher's forms are done, the pupil's
 * status chip, and the pathways their forms belong to (referral first).
 */
export function teacherPupilSummary(child) {
  const forms = (child && child.forms) || [];
  const done = forms.filter(isTeacherFormDone).length;
  const total = forms.length;
  const started = forms.some((f) => teacherFormStatus(f) !== 'notStarted');
  const pathways = ['referral', 'sen'].filter((p) => forms.some((f) => (f.pathway || 'referral') === p));
  return {
    done,
    total,
    allDone: total > 0 && done === total,
    chip: total > 0 && done === total ? 'done' : started ? 'inProgress' : 'notStarted',
    pathways,
    pathwayLabels: pathways.map((p) => PATHWAY_LABELS[p]),
  };
}

/**
 * The list split as the page shows it: pupils with forms still to do, then
 * "Done" (every form finished) at the bottom; each in name order.
 */
export function groupTeacherPupils(children) {
  const byName = (a, b) => String(a.pupilName || '').localeCompare(String(b.pupilName || ''));
  const list = [...(children || [])].filter((c) => (c.forms || []).length > 0).sort(byName);
  return {
    open: list.filter((c) => !teacherPupilSummary(c).allDone),
    done: list.filter((c) => teacherPupilSummary(c).allDone),
  };
}

/**
 * The pupil page's rows with the sequential lock ("Answer one form at a
 * time. The next one opens when you finish the one before. Finished forms
 * stay open."): a form is open once started or finished, and the first one
 * not finished is open too.
 */
export function teacherFormRows(forms) {
  const rows = (forms || []).map((f) => ({ ...f, rowStatus: teacherFormStatus(f) }));
  const firstNotDone = rows.find((r) => r.rowStatus !== 'done');
  return rows.map((r) => ({ ...r, open: r.rowStatus !== 'notStarted' || r === firstNotDone }));
}

/**
 * The SENCO's note on the pupil page (TDI-620 wording). The SENCO's name is
 * the one who gave the forms; without it the note still names the school.
 */
export function sencoNote({ sencoName, schoolName, pupilName }) {
  const who = sencoName
    ? `${sencoName}, the SENCO at ${schoolName || 'your school'},`
    : `The SENCO at ${schoolName || 'your school'}`;
  return `${who} has asked you to answer some questions about ${pupilName}. Your answers save as you go, so you can stop and carry on later.`;
}
